import { ApolloLink } from "@apollo/client";
import axios from "axios";
import Cookies from "js-cookie";
import qs from "query-string";
import { Tracker } from "./analytics2";

interface ShareASaleEvent {
  amount: number;
  currency: string;
  tracking: string;
}

const SHAREASALE_MERCHANT_ID = 80059;

function trackShareSale({ amount, currency, tracking }: ShareASaleEvent) {
  const queryString = qs.stringify({
    amount,
    currency,
    tracking,
    transtype: "sale",
    merchantID: SHAREASALE_MERCHANT_ID,
  });

  const src = `https://shareasale.com/sale.cfm?${queryString}`;

  if (process.env.ENVIRONMENT === "production") {
    const image = document.createElement("img");
    image.width = 1;
    image.height = 1;
    image.src = src;
  } else {
    console.warn("[dev-mode] track-share-sale", src); // eslint-disable-line
  }
}

type TrackAwinParams = {
  amount: number;
  currency: string;
  bookingId: string;
  customerAcquisition: "NEW" | "RETURNING";
  couponCode?: string;
};

function trackAwin(params: TrackAwinParams) {
  trackViaPixel();
  trackViaServer();

  function trackViaPixel() {
    const queryParams: Record<string, string> = {
      amount: String(params.amount),
      cr: params.currency,
      ref: params.bookingId,
      parts: `DEFAULT:${params.amount}`,
      customeracquisition: params.customerAcquisition,
    };
    if (params.couponCode) queryParams.vc = params.couponCode;
    const queryString = qs.stringify(queryParams);

    const src = `https://www.awin1.com/sread.img?tt=ns&tv=2&merchant=118093&ch=aw&${queryString}`;

    if (process.env.ENVIRONMENT === "production") {
      // 1. Add invisible tracking pixel
      const image = document.createElement("img");
      image.src = src;
      image.border = "0";
      image.width = 0;
      image.height = 0;
      document.body.appendChild(image);

      // 2. Add Awin Tracking Script
      const script = document.createElement("script");
      script.type = "text/javascript";
      const voucherLine = params.couponCode
        ? `AWIN.Tracking.Sale.voucher = "${params.couponCode}";\n`
        : "";
      script.innerHTML = `
      var AWIN = AWIN || {};
      AWIN.Tracking = AWIN.Tracking || {};
      AWIN.Tracking.Sale = {};
      AWIN.Tracking.Sale.amount = "${params.amount}";
      AWIN.Tracking.Sale.orderRef = "${params.bookingId}";
      AWIN.Tracking.Sale.parts = "DEFAULT:${params.amount}";
      ${voucherLine}
      AWIN.Tracking.Sale.currency = "${params.currency}";
      AWIN.Tracking.Sale.channel = "aw";
      AWIN.Tracking.Sale.customerAcquisition = "${params.customerAcquisition}";
    `;
      document.body.appendChild(script);
    } else {
      console.warn("[dev-mode] track-awin pixel", src); // eslint-disable-line
    }
  }

  async function trackViaServer() {
    const baseUrl =
      "https://www.awin1.com/sread.php?tt=ss&tv=2&merchant=118093";

    const awcCookie = Cookies.get("awc");

    const url = new URL(baseUrl);
    url.searchParams.set("amount", String(params.amount));
    url.searchParams.set("ch", "aw");
    url.searchParams.set("cr", params.currency);
    url.searchParams.set("ref", params.bookingId);
    url.searchParams.set("parts", `DEFAULT:${String(params.amount)}`);
    url.searchParams.set("customeracquisition", params.customerAcquisition);
    if (params.couponCode) url.searchParams.set("vc", params.couponCode);
    if (awcCookie) url.searchParams.set("cks", awcCookie);

    try {
      if (process.env.ENVIRONMENT === "production") {
        // only send for real on production
        await axios.get(url.toString(), { timeout: 10000 });
      } else {
        console.warn("[dev-mode] track-awin server", url.toString());
      }
    } catch (err) {
      console.error("Awin S2S tracking failed", err);
    }
  }
}

interface PurchaseData {
  productId: string;
  productName: string;
  orderId: string;
  amountInMajorUnits: number;
  currencyCode: string;
  email: string;
  phoneNumber: string | undefined | null;
  couponCode?: string;
  isReturningCustomer: boolean;
}

export function trackTransaction({
  productId,
  productName,
  orderId,
  amountInMajorUnits,
  currencyCode,
  email,
  phoneNumber,
  couponCode,
  isReturningCustomer,
}: PurchaseData) {
  trackShareSale({
    amount: amountInMajorUnits,
    currency: currencyCode,
    tracking: orderId,
  });

  trackAwin({
    amount: amountInMajorUnits,
    currency: currencyCode,
    bookingId: orderId,
    customerAcquisition: isReturningCustomer ? "RETURNING" : "NEW",
    couponCode,
  });

  if (window && typeof window.gtag === "function") {
    window.gtag("set", "user_data", {
      email: email,
      phone_number: phoneNumber,
    });

    window.gtag("event", "purchase", {
      transaction_id: orderId,
      value: amountInMajorUnits,
      currency: currencyCode,
      items: [
        {
          item_id: productId,
          item_name: productName,
          item_brand: "MagicalTrip",
          price: amountInMajorUnits,
          quantity: 1,
        },
      ],
    });
    window.gtag("event", "conversion", {
      send_to: "AW-877375277/25O3CJ2t4PIYEK3erqID",
      value: amountInMajorUnits,
      currency: currencyCode,
    });
  }

  if (window && typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      value: amountInMajorUnits,
      currency: currencyCode,
    });
  }
}

export const createApolloAnalyticsLink = (tracker: Tracker) =>
  new ApolloLink((operation, forward) => {
    if (!forward) {
      return null;
    }
    return forward(operation);
  });
